import { FeatureTags, FeatureTag, ALL_FEATURE_TAGS } from '../constants/featureTags'

const normalize = (value?: string) => value?.toLowerCase().trim()

const resolveFeatureTag = (rawTag?: string): FeatureTag | undefined => {
  const tag = normalize(rawTag)
  if (!tag) return undefined
  return ALL_FEATURE_TAGS.find((feature) => feature.toLowerCase() === tag) as FeatureTag | undefined
}

const isActiveStatus = (status?: string) => {
  const s = normalize(status)
  return s === 'member' || s === 'trial' || s === 'free'
}

export interface ComputeEffectiveTagsOptions {
  status?: string
  productTags?: FeatureTag[]
  productDenyTags?: FeatureTag[]
  userTags?: string[]
  denyTags?: string[]
  isAdmin?: boolean
  tradingProductEnabled?: boolean
  gtfStatus?: string
}

/**
 * Computes the effective feature tags for a user based on:
 * - GTF status (active GTF members get DTU indicators)
 * - Status (canceled/hold users get no features)
 * - Product default tags
 * - User added tags (products.tags)
 * - User denied tags (products.denyTags)
 * - Trading product enabled status
 */
export const computeEffectiveTags = (options: ComputeEffectiveTagsOptions): Set<FeatureTag> => {
  const normalizedStatus = normalize(options.status)
  if (normalizedStatus === 'canceled' || normalizedStatus === 'hold') {
    return new Set<FeatureTag>([])
  }

  const effective = new Set<FeatureTag>()

  if (isActiveStatus(options.gtfStatus)) {
    effective.add(FeatureTags.DTU_INDICATORS)
  }

  // Product defaults: add tags then remove denyTags
  ;(options.productTags || []).forEach((t) => {
    const f = resolveFeatureTag(t)
    if (f) effective.add(f)
  })
  ;(options.productDenyTags || []).forEach((t) => {
    const f = resolveFeatureTag(t)
    if (f) effective.delete(f)
  })

  // User overrides: add user tags, then remove user denyTags
  ;(options.userTags || []).forEach((t) => {
    const f = resolveFeatureTag(t)
    if (f) effective.add(f)
  })

  if (options.tradingProductEnabled) {
    effective.add(FeatureTags.AUTOTRADING)
    effective.add(FeatureTags.MANAGE_TRADING_ACCOUNTS)
  }

  ;(options.denyTags || []).forEach((t) => {
    const f = resolveFeatureTag(t)
    if (f) effective.delete(f)
  })

  return effective
}

/**
 * Determines the source of a tag for a user
 * Returns: 'product' | 'user' | 'denied' | 'none'
 */
export type TagSource = 'product' | 'user' | 'denied' | 'none'

export const getTagSource = (
  tag: FeatureTag,
  productTags: FeatureTag[],
  userTags: string[],
  denyTags: string[]
): TagSource => {
  const normalizedTag = tag.toLowerCase()
  const normalizedUserTags = userTags.map((t) => t.toLowerCase())
  const normalizedDenyTags = denyTags.map((t) => t.toLowerCase())

  if (normalizedDenyTags.includes(normalizedTag)) {
    return 'denied'
  }
  if (normalizedUserTags.includes(normalizedTag)) {
    return 'user'
  }
  if (productTags.includes(tag)) {
    return 'product'
  }
  return 'none'
}

/**
 * Determines if a tag is currently enabled for a user
 */
export const isTagEnabled = (tag: FeatureTag, effectiveTags: Set<FeatureTag>): boolean => {
  return effectiveTags.has(tag)
}
